import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { OrderResponseRecord, PaymentSplit, PosTicketRequest, ReciboEstado, ReciboMotivo } from '../models/api.models';
import { PrinterService, ErrorDeImpresion, EstadoAgente, ResultadoImpresion } from './printer.service';
import { OrderService } from './order.service';
import { TableService } from './table.service';
import { OfflineOrderService } from '../core/offline/offline-order.service';
import { SessionService } from '../core/tenant/session.service';
import { TirillasPendientesStore } from '../core/offline/tirillas-pendientes.store';
import { negocioDeLaSesionAbierta } from '../core/tenant/negocio-del-token';
import { CierreImprimible, htmlDeAperturaDeCajon, htmlDeCierre, htmlDeComanda, htmlDeCotizacion, htmlDeRemision, htmlDeTirilla, lineasDeSaldoAFavor, lineasDeVentaPosteriorAlInicio } from '../core/impresion/documentos-imprimibles';
import { desglosePorTarifa } from '../core/impresion/impuestos-del-papel';
import { Cotizacion, fechaCorta } from '../core/cotizaciones/cotizaciones';
import type { Remision } from '../core/remisiones/remisiones';
import { imprimirEnNavegador } from '../core/impresion/imprimir-en-navegador';
import { aplicarComprobante, papelVacio } from '../core/impresion/papel-de-venta';
import type { FacturaEnElPapel } from '../core/impresion/factura-en-el-papel';
import { MenuService } from './menu.service';
import { pesos } from '../core/ui/pesos';
import { resultadoDelCierre } from '../core/finance/resultado-del-cierre';
import { PagerConfigService } from './pager-config.service';

/** Diseño 22: por dónde imprime este equipo. */
export type ViaDeImpresion = 'agente' | 'navegador';

/**
 * Diseño 22 §3 (decisión de Santiago): con tirilla, el cajón se abre solo si entra
 * efectivo: CASH, o MIXED con alguna parte en efectivo. QR, tarjeta, transferencia
 * y crédito imprimen sin abrirlo. Solo lo cumple la vía agente: en la vía navegador
 * el driver decide y queda sujeto a la medición en la caja (filas I-K).
 */
export function entraEfectivo(medio: string | null | undefined, pagos?: { method: string; amount: number }[] | null): boolean {
    if (medio === 'CASH') return true;
    if (medio === 'MIXED') return (pagos ?? []).some((p) => p.method === 'CASH' && p.amount > 0);
    return false;
}

/** Referencia a una venta para dejar constancia de su tirilla. */
export interface RefDeVenta {
    /** Id del servidor, si la venta ya sincronizó. */
    idOrder?: number | null;
    /** Clave local (Dexie), si la venta nació en este POS. */
    clave?: string | null;
}

/** Una venta cobrada cuya tirilla no salió (docs/arquitectura/21 §2.2). */
export interface VentaSinTirilla extends RefDeVenta {
    /** Lo que se ve en la lista. */
    numero: string;
    hora: string;
    total: number;
    motivo: ReciboMotivo;
    detalle?: string;
    /** Datos con los que se imprime, si la venta no está en el servidor. */
    imprimible?: OrderResponseRecord;
    /** Ticket original (efectivo, cambio, factura) para reimprimir igual. */
    ticket?: PosTicketRequest;
    /** F4.9 — un recibo de caja de cartera, no una venta: se reimprime con su ticket tal cual. */
    tipo?: 'recibo_cartera';
}

/** F4.9 — lo que lleva el recibo de caja de un abono a cartera. */
export interface ReciboDeCarteraImprimible {
    id: string;
    numero: number;
    ocurridoEn: string;
    clienteNombre: string;
    clienteDocumento: string;
    monto: number;
    /** `EFECTIVO` abre el cajón; cualquier otro medio, no. */
    medio: string;
    /** Como se lee en el papel («Efectivo», «Transferencia»…). */
    medioEtiqueta: string;
    /** Saldo releído del servidor tras el abono; null si no se pudo leer (la línea no sale). */
    saldoTras: number | null;
    cobradoPor: string | null;
    /** F4.12 B2: lo que de este recibo quedó a favor del cliente. Con 0 o sin dato, la línea no sale. */
    saldoAFavor?: number | null;
}

export interface OpcionesTirilla {
    /** `false` al reimprimir: si el cliente ya se fue, el cajón abierto es un hueco. */
    abrirCajon: boolean;
    esFactura?: boolean;
    cashGiven?: number;
    change?: number;
    /** Sobrescribe el número (p. ej. `PEND-00003` en ventas sin sincronizar). */
    ticketNumber?: string;
    /** Se antepone al pie (p. ej. «PENDIENTE DE SINCRONIZAR»). */
    prefijoPie?: string;
    /** El cliente de la factura electrónica; sin él, la tirilla dice «Consumidor Final». */
    cliente?: { nombre: string; documento: string };
    /** F4.12 B3: `saldoAFavorAplicado` de crear orden, tal cual; con 0 o null no sale nada. */
    saldoAFavorAplicado?: number | null;
    /** F4.12 B3: `saldoAFavorRestante` de crear orden; sale bajo «Queda a crédito» solo si es mayor que 0. */
    saldoAFavorRestante?: number | null;
    /** F4.13 (c), B10: venta a crédito después del inicio (de la respuesta, o deducida sin red); null = no sale. */
    ventaPosteriorAlInicio?: { venceEl: string | null } | null;
    /** M34 A7: el reparto del pago mixto, para desglosarlo en el papel. Al reimprimir no se conoce y no sale. */
    pagos?: PaymentSplit[] | null;
    /** M34 A7: la factura electrónica pedida (emitida o en trámite). Solo la pinta la vía del navegador. */
    factura?: FacturaEnElPapel | null;
    /** M34 B2: la propina cobrada (al cobrar); al reimprimir sale de la orden. */
    propina?: number | null;
}

/** Texto para el cajero por motivo (docs/arquitectura/21 §1). */
export const TEXTO_MOTIVO: Record<ReciboMotivo, string> = {
    agente_apagado: 'El programa de impresión no está abierto en este computador',
    navegador_pide_permiso: 'Chrome está pidiendo permiso arriba: pulsa Permitir',
    agente_no_responde: 'El programa de impresión no responde',
    impresora_sin_conexion: 'La impresora está apagada o desconectada',
    en_cola: 'La impresora recibió el ticket y no lo imprimió. Revisa papel y cable',
    error_agente: 'El programa de impresión devolvió un error',
    dialogo_cancelado: 'Se canceló la impresión',
};

/**
 * El ÚNICO punto de entrada para imprimir en el POS (docs/arquitectura/21).
 *
 * Las pantallas piden «imprime esta tirilla» y reciben un resultado honesto
 * (`enviado`, `confirmado`, `no_impreso` + motivo). Cómo se imprime —hoy el
 * agente local por `PrinterService`; mañana quizá `window.print()`— es asunto
 * de este servicio y de nadie más.
 *
 * También lleva la contabilidad de lo que NO salió: `pendientes` es la lista
 * de ventas de hoy cobradas sin tirilla, que la cabecera del POS muestra como
 * «N sin tirilla» hasta que se reimpriman o alguien diga que no hacen falta.
 *
 * Reglas: la venta nunca se bloquea por esto; la pantalla no afirma lo que
 * no se comprobó; lo no impreso queda a la vista.
 */
@Injectable({ providedIn: 'root' })
export class ImpresionService implements OnDestroy {
    private printer = inject(PrinterService);
    private readonly session = inject(SessionService);
    private orders = inject(OrderService);
    private tableService = inject(TableService);
    private menu = inject(MenuService);
    private rastreadores = inject(PagerConfigService);
    private offline = inject(OfflineOrderService);
    /** F2.5 — la lista «sin tirilla» se guarda por negocio para sobrevivir a recargar. */
    private guardadas = inject(TirillasPendientesStore);

    static readonly INTERVALO_SONDEO_MS = 30_000;
    static readonly CLAVE_VIA = 'suresell.impresion.via';

    // ------------------------------------------------------------------ vía

    private viaDecidida: Promise<ViaDeImpresion> | null = null;
    /** La vía ya decidida (para pintar el ajuste); null mientras se decide. */
    readonly viaActual = signal<ViaDeImpresion | null>(null);

    /**
     * Decisión de Santiago (04/10): por ahora todo se imprime por el navegador. Con `AGENTE_EN_USO` en false no se
     * consulta el agente (localhost:8181) al arrancar —así Chrome no pide permiso de red local— y un equipo que tenía
     * guardado «agente» pasa a «navegador» sin preguntar. Se acepta perder el cajón solo con efectivo y la constancia
     * del spooler. El agente y `PrinterService` se quedan, con sus pruebas: volver a usarlo es poner esto en true.
     *
     * Con el agente en uso (diseño 22, regla acordada con C): 1) el valor guardado en el equipo manda; 2) sin valor y
     * el agente responde → agente, y se guarda; 3) sin valor y el agente no responde → navegador, SIN guardar;
     * 4) almacenamiento ilegible → se detecta cada vez, sin guardar.
     */
    static AGENTE_EN_USO = false;

    via(): Promise<ViaDeImpresion> {
        if (!this.viaDecidida) {
            const decision = this.decidirVia().then((v) => {
                // Si el cajero eligió mientras se detectaba, manda lo que eligió.
                if (this.viaDecidida === decision) this.viaActual.set(v);
                return v;
            });
            this.viaDecidida = decision;
        }
        return this.viaDecidida;
    }

    /** El ajuste del equipo («Imprimir con el navegador»): se guarda y manda desde ya. */
    elegirVia(v: ViaDeImpresion): void {
        if (v === 'agente' && !ImpresionService.AGENTE_EN_USO) return; // 04/10: el agente no se usa
        try { localStorage.setItem(ImpresionService.CLAVE_VIA, v); } catch { /* solo esta sesión */ }
        this.viaDecidida = Promise.resolve(v);
        this.viaActual.set(v);
        if (v === 'navegador') this.detenerSondeo();
        else this.iniciarSondeo();
    }

    private async decidirVia(): Promise<ViaDeImpresion> {
        if (!ImpresionService.AGENTE_EN_USO) {
            try {
                if (localStorage.getItem(ImpresionService.CLAVE_VIA) === 'agente') {
                    localStorage.setItem(ImpresionService.CLAVE_VIA, 'navegador');
                }
            } catch { /* ilegible: da igual, la vía es la misma */ }
            return 'navegador';
        }
        let guardada: string | null = null;
        let sePuedeGuardar = true;
        try {
            guardada = localStorage.getItem(ImpresionService.CLAVE_VIA);
        } catch {
            sePuedeGuardar = false;
        }
        if (guardada === 'agente' || guardada === 'navegador') return guardada;
        let responde = false;
        try {
            responde = !!(await this.printer.consultarEstado())?.conectado;
        } catch {
            responde = false;
        }
        if (!responde) return 'navegador';
        if (sePuedeGuardar) {
            try { localStorage.setItem(ImpresionService.CLAVE_VIA, 'agente'); } catch { /* se detecta otra vez */ }
        }
        return 'agente';
    }

    private async porNavegador(): Promise<boolean> {
        return (await this.via()) === 'navegador';
    }

    /** Diseño 22 §3b — cierre de caja imprimible, con firmas. null si la sede no imprime. */
    async imprimirCierre(c: CierreImprimible): Promise<ResultadoImpresion | null> {
        if (!this.activa()) return null;
        if (await this.porNavegador()) return imprimirEnNavegador.imprimir(htmlDeCierre(c));
        // Vía agente: el agente solo sabe imprimir tickets; el cierre va como líneas de un ticket.
        const n = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);
        const si = (etiqueta: string, v: number | null | undefined): [string, number][] => (n(v) ? [[etiqueta, v]] : []);
        // 15/10: el resultado por el NETO, con los mismos textos que el papel del navegador y la pantalla del cierre.
        const r = resultadoDelCierre({ shortages: c.faltantes, sobrantes: c.sobrantes });
        // Z35 (Santiago 18/10; TEXTOS 42e2a4f): con neto ≥ 0 solo «La caja cerró bien»: sin sobrante, detalle, esperado ni diferencia.
        const falta = r.neto < 0;
        const lineas: [string, number][] = [
            ...(n(c.totalVentas) ? [[`Número de ventas: ${c.totalVentas}`, 0] as [string, number]] : []),
            ...si('Ventas en efectivo', c.ventas?.efectivo), ...si('Ventas con tarjeta', c.ventas?.tarjeta), ...si('Ventas con QR', c.ventas?.qr),
            // B5b (ECM): crédito y cartera solo con valor, como en la vía navegador.
            ...(n(c.vendidoACredito) && c.vendidoACredito > 0 ? [['Vendido a crédito', c.vendidoACredito] as [string, number]] : []),
            ...(n(c.recaudoCarteraEfectivo) && c.recaudoCarteraEfectivo > 0 ? [['Abonos recibidos en efectivo', c.recaudoCarteraEfectivo] as [string, number]] : []),
            ...(typeof c.recaudoComoSaldoAFavorEfectivo === 'number' && c.recaudoComoSaldoAFavorEfectivo > 0
                ? [['Recibido como saldo a favor', c.recaudoComoSaldoAFavorEfectivo] as [string, number]] : []),
            ...(typeof c.devolucionesSaldoAFavorEfectivo === 'number' && c.devolucionesSaldoAFavorEfectivo > 0
                ? [['Devoluciones de saldo a favor', -c.devolucionesSaldoAFavorEfectivo] as [string, number]] : []),
            ...c.gastos.map((g) => [`Gasto: ${g.concepto}`, g.monto] as [string, number]),
            ...(n(c.gastosTotal) && c.gastosTotal > 0 ? [['Total de gastos', -c.gastosTotal] as [string, number]] : []),
            ...si('Base inicial', c.baseInicial),
            ...(falta ? si('Efectivo esperado', c.efectivoEsperado) : []),
            ['Efectivo contado', n(c.efectivoContado) ? c.efectivoContado : c.contado.efectivo],
            ...(falta ? si('Diferencia', c.diferenciaEfectivo) : []),
            ['Tarjeta contada', c.contado.tarjeta], ['QR contado', c.contado.qr],
            ['Base que queda', c.baseQueQueda],
        ];
        const comoOrden = {
            idOrder: 0, createdAt: c.cerradoEn.toISOString(), subtotal: 0, total: 0, paymentMethod: 'CASH',
            items: lineas.map(([nombre, valor]) => ({ productId: nombre, nameProduct: nombre, quantity: 1, unitPrice: valor, totalPrice: valor, instructions: null, comboGroup: null })),
        } as unknown as OrderResponseRecord;
        const ticket = this.printer.buildTicketRequest(comoOrden, false);
        ticket.ticketNumber = `CIERRE-T${c.turno ?? ''}`;
        ticket.cashierName = c.cajero;
        ticket.abrirCajon = false;
        const resultado = falta
            ? [`Faltante: ${pesos(-r.neto)}`, ...(r.descuadrado ? ['Diferencias al cerrar', ...r.filas] : [])]
            : ['La caja cerró bien'];
        ticket.footerMessage = ['CIERRE DE CAJA', ...resultado,
            '', 'Firma de quien cierra: ____________', '', 'Firma de quien recibe: ____________'].join('\n');
        return this.ejecutar(() => this.printer.printTicket(ticket));
    }

    /** Estado del agente/impresora, con nombre honesto. */
    readonly estadoAgente = computed<EstadoAgente | null>(() => this.printer.estado());
    /** La sede imprime tirilla; sin esto no hay sondeo, indicador ni marcador. */
    readonly activa = computed(() => this.tableService.imprimeTirilla());

    /** Ventas de hoy cobradas sin tirilla. */
    readonly pendientes = signal<VentaSinTirilla[]>([]);
    readonly cantidadPendientes = computed(() => this.pendientes().length);

    private sondeoTimer: ReturnType<typeof setInterval> | null = null;
    private onVisible = () => {
        if (typeof document === 'undefined' || document.visibilityState === 'visible') {
            void this.sondear();
        }
    };

    // ---------------------------------------------------------------- estado

    /**
     * Arranca el sondeo periódico del agente: cada 30 s, al volver el foco a
     * la pestaña y tras cada intento de impresión. Idempotente.
     */
    iniciarSondeo(): void {
        if (this.sondeoTimer) return;
        // Vía navegador: no hay agente al que sondear.
        void this.via().then((v) => { if (v === 'agente') this.arrancarSondeo(); });
    }

    private arrancarSondeo(): void {
        if (this.sondeoTimer) return;
        void this.sondear();
        this.sondeoTimer = setInterval(() => void this.sondear(), ImpresionService.INTERVALO_SONDEO_MS);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.onVisible);
        }
    }

    /**
     * El sondeo (intervalo de 30 s y escucha de visibilidad) muere con el servicio. En la app es root y vive lo que la
     * página; en las pruebas cada inyector se destruye y, sin esto, el intervalo seguía disparando en medio de otra
     * prueba (hipótesis del NG0100 intermitente del sidebar, 23/09: compatible, no demostrada).
     */
    ngOnDestroy(): void {
        this.detenerSondeo();
    }

    detenerSondeo(): void {
        if (this.sondeoTimer) {
            clearInterval(this.sondeoTimer);
            this.sondeoTimer = null;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.onVisible);
        }
    }

    /** Sondeo automático: cero llamadas si la sede no imprime; respeta el corte por fallos. */
    async sondear(): Promise<EstadoAgente | null> {
        if (!this.activa()) return null;
        if (await this.porNavegador()) return null;
        return this.printer.sondear();
    }

    /** Consulta bajo demanda (clic humano): siempre intenta. */
    async consultarEstado(): Promise<EstadoAgente | null> {
        if (!this.activa()) return null;
        if (await this.porNavegador()) return null;
        this.printer.reiniciarBusqueda();
        return this.printer.consultarEstado();
    }

    // -------------------------------------------------------------- imprimir

    /**
     * Imprime la tirilla de una venta. Nunca lanza: devuelve el resultado, y
     * deja constancia (`recibo_estado`) si se le pasa la referencia.
     */
    async imprimirTirilla(orden: OrderResponseRecord, opciones: OpcionesTirilla, ref?: RefDeVenta): Promise<ResultadoImpresion> {
        // M34 A7: la plantilla de A3 solo en la vía del navegador; el agente está apagado y su plantilla es A8.
        const navegador = await this.porNavegador();
        const comprobante = navegador ? this.tableService.modo()?.comprobante ?? null : null;
        const ticket = comprobante
            ? this.printer.buildTicketRequest(orden, opciones.esFactura ?? false, opciones.cashGiven, opciones.change, comprobante.pie ?? null)
            : this.printer.buildTicketRequest(orden, opciones.esFactura ?? false, opciones.cashGiven, opciones.change);
        ticket.abrirCajon = opciones.abrirCajon;
        if (opciones.ticketNumber) ticket.ticketNumber = opciones.ticketNumber;
        if (opciones.cliente) {
            ticket.customerName = opciones.cliente.nombre;
            ticket.customerId = opciones.cliente.documento;
        }
        if (opciones.prefijoPie) {
            ticket.footerMessage = `${opciones.prefijoPie}${ticket.footerMessage ? ' — ' + ticket.footerMessage : ''}`;
        }
        const trasElPago = [
            ...lineasDeVentaPosteriorAlInicio(opciones.ventaPosteriorAlInicio),
            ...lineasDeSaldoAFavor(orden.total, opciones.saldoAFavorAplicado, opciones.saldoAFavorRestante),
        ];
        // Vía navegador: `window.print()` no dice si salió; no hay constancia ni lista (decisión de Santiago).
        if (navegador) {
            // El catálogo solo hace falta para el desglose, y la impresión no espera a la red más de 1,5 s.
            const catalogo = comprobante?.mostrar?.desglosePorTarifa ? await this.menu.tarifas(1500) : [];
            aplicarComprobante(ticket, orden, {
                comprobante, catalogo, producto: (id) => this.menu.producto(id),
                conMeseros: this.tableService.conMeseros(), pagos: opciones.pagos,
            });
            if (opciones.factura) ticket.papel = { ...(ticket.papel ?? papelVacio()), factura: opciones.factura };
            // M34 B2: la propina, salvo que el comercio la desmarque en «Comprobante».
            const propina = Number(opciones.propina ?? orden.propina ?? 0);
            if (propina > 0 && comprobante?.mostrar?.propina !== false) ticket.papel = { ...(ticket.papel ?? papelVacio()), propina };
            return imprimirEnNavegador.imprimir(htmlDeTirilla(ticket, trasElPago));
        }
        // Vía agente: no conoce campos nuevos; las líneas van al pie, detrás de la leyenda (B-08).
        if (trasElPago.length) ticket.footerMessage = [ticket.footerMessage, ...trasElPago].filter(Boolean).join('\n');
        const resultado = await this.ejecutar(() => this.printer.printTicket(ticket));
        await this.anotarResultado(ref, resultado, orden, ticket);
        return resultado;
    }

    /** F4.9 — recibo de caja de un abono. null si la sede no imprime. Nunca lanza. Reimprimir no abre el cajón. */
    async imprimirReciboDeCartera(r: ReciboDeCarteraImprimible, opciones: { reimpresion?: boolean } = {}): Promise<ResultadoImpresion | null> {
        if (!this.activa()) return null;
        const comoOrden = {
            idOrder: 0,
            createdAt: r.ocurridoEn,
            subtotal: r.monto,
            total: r.monto,
            paymentMethod: r.medio,
            items: [{ productId: 'abono-cartera', nameProduct: 'Abono', quantity: 1, unitPrice: r.monto, totalPrice: r.monto, instructions: null, comboGroup: null }],
        } as unknown as OrderResponseRecord;
        const ticket = this.printer.buildTicketRequest(comoOrden, false);
        ticket.ticketNumber = `RC-${r.numero}`;
        ticket.dateTime = r.ocurridoEn;
        ticket.paymentMethod = r.medioEtiqueta;
        ticket.customerName = r.clienteNombre;
        ticket.customerId = r.clienteDocumento;
        if (r.cobradoPor) ticket.cashierName = r.cobradoPor;
        ticket.cashGiven = 0;
        ticket.change = 0;
        // El cajón va con el ticket, como en la venta: solo entra dinero al cajón en efectivo.
        ticket.abrirCajon = r.medio === 'EFECTIVO' && !opciones.reimpresion;
        const cabeza = ['RECIBO DE CAJA'];
        if (typeof r.saldoAFavor === 'number' && Number.isFinite(r.saldoAFavor) && r.saldoAFavor > 0) {
            // Centavos a propósito: el formato viejo REDONDEABA y `pesos()` sin centavos TRUNCA, así que la cifra
            // impresa habría bajado hasta un peso sin que nadie lo viera. Con centavos no se pierde nada (F1.16).
            cabeza.push(`Saldo a favor: ${pesos(r.saldoAFavor, { centavos: true })}`);
        }
        if (typeof r.saldoTras === 'number' && Number.isFinite(r.saldoTras)) {
            cabeza.push(`Queda debiendo: ${pesos(r.saldoTras, { centavos: true })}`);
        }
        ticket.footerMessage = [...cabeza, ticket.footerMessage].filter((t) => t && t.trim().length > 0).join('\n');

        if (await this.porNavegador()) return imprimirEnNavegador.imprimir(htmlDeTirilla(ticket));
        const resultado = await this.ejecutar(() => this.printer.printTicket(ticket));
        const ref: RefDeVenta = { clave: `recibo-cartera:${r.id}` };
        if (resultado.estado === 'no_impreso') {
            await this.anotarPendiente({
                ...ref,
                numero: ticket.ticketNumber,
                hora: r.ocurridoEn,
                total: r.monto,
                motivo: resultado.motivo ?? 'error_agente',
                detalle: resultado.detalle,
                ticket,
                tipo: 'recibo_cartera',
            });
        } else {
            await this.retirarPendiente(ref);
        }
        return resultado;
    }

    /** Imprime la comanda de cocina. Nunca lanza. */
    /**
     * M34 D1 · el papel de una cotización, por el navegador (el agente está apagado; su plantilla es A8). Sin cajón. El
     * bloque de impuestos sale del catálogo y de la tarifa de cada producto, como la tirilla sin red.
     */
    async imprimirCotizacion(c: Cotizacion): Promise<ResultadoImpresion> {
        const catalogo = await this.menu.tarifas(1500);
        const impuestos = desglosePorTarifa(c.lineas.map((l) => ({
            impuestoCodigo: this.menu.producto(l.productoId)?.impuestoCodigo ?? null, cobrado: l.total, cantidad: l.cantidad,
        })), catalogo);
        const s = this.session.session();
        return imprimirEnNavegador.imprimir(htmlDeCotizacion({
            negocio: s?.tenantName ?? '', nit: s?.nit ?? null, numero: c.numero,
            fecha: new Date(c.creadoEn).toLocaleDateString('es-CO'), validaHasta: fechaCorta(c.validaHasta),
            clienteNombre: c.clienteNombre, clienteDocumento: c.clienteDocumento,
            lineas: c.lineas, total: c.total, impuestos,
        }));
    }

    /**
     * M34 D2 · el papel de una remisión, por el navegador, sin cajón. La sede y «Entregar en» son los de la remisión, no
     * los de la caja que imprime.
     */
    async imprimirRemision(r: Remision): Promise<ResultadoImpresion> {
        const s = this.session.session();
        return imprimirEnNavegador.imprimir(htmlDeRemision({
            negocio: s?.tenantName ?? '', nit: s?.nit ?? null, numero: r.numero, fecha: new Date(r.creadoEn).toLocaleDateString('es-CO'),
            clienteNombre: r.clienteNombre, clienteDocumento: r.clienteDocumento, lineas: r.lineas ?? [],
            sede: r.sedeNombre ?? null, entregarEn: r.nota ?? null,
        }));
    }

    async imprimirComanda(orden: OrderResponseRecord): Promise<ResultadoImpresion> {
        if (await this.porNavegador()) return imprimirEnNavegador.imprimir(htmlDeComanda(orden, this.rastreadores.groups()));
        return this.ejecutar(() => this.printer.printOrderTicketEmergency(orden));
    }

    /**
     * Z31: la comanda del pedido del personal, con «Personal · {persona}». null por agente: su plantilla no lleva el
     * encabezado (Z40), y un papel sin él no dice para quién es (condición de DrDev002): ahí la cocina usa el KDS.
     */
    async imprimirComandaDelPersonal(orden: OrderResponseRecord, persona: string): Promise<ResultadoImpresion | null> {
        if (!(await this.porNavegador())) return null;
        return imprimirEnNavegador.imprimir(htmlDeComanda(orden, this.rastreadores.groups(), persona));
    }

    /** Ticket corto de prueba, sin cajón. */
    async probar(): Promise<ResultadoImpresion> {
        const ticket = this.printer.buildTicketRequest({
            idOrder: 0,
            createdAt: new Date().toISOString(),
            subtotal: 0,
            total: 0,
            paymentMethod: 'CASH',
            items: [{ productId: 'prueba', nameProduct: 'Prueba de impresión', quantity: 1, unitPrice: 0, totalPrice: 0, instructions: null, comboGroup: null }],
        } as unknown as OrderResponseRecord);
        ticket.ticketNumber = 'PRUEBA';
        ticket.footerMessage = 'Si lees esto, la impresión funciona.';
        ticket.abrirCajon = false;
        if (await this.porNavegador()) return imprimirEnNavegador.imprimir(htmlDeTirilla(ticket));
        return this.ejecutar(() => this.printer.printTicket(ticket));
    }

    /**
     * Abre el cajón (conteo de cierre, barra lateral). Nunca lanza. Por el navegador no
     * hay forma de abrirlo sin imprimir: sale el comprobante «APERTURA DE CAJÓN» y el
     * driver lo abre (decisión de Santiago: «muy buen feature»).
     */
    async abrirCajon(datos: { turno?: number | null } = {}): Promise<ResultadoImpresion> {
        if (await this.porNavegador()) {
            const s = this.session.session();
            return imprimirEnNavegador.imprimir(htmlDeAperturaDeCajon({
                negocio: s?.tenantName ?? '', turno: datos.turno ?? null, cajero: s?.userName ?? '', momento: new Date(),
            }));
        }
        return this.ejecutar(() => this.printer.openDrawer());
    }

    private async ejecutar(accion: () => Promise<ResultadoImpresion>): Promise<ResultadoImpresion> {
        let resultado: ResultadoImpresion;
        try {
            resultado = await accion();
        } catch (e) {
            resultado = e instanceof ErrorDeImpresion
                ? { estado: 'no_impreso', motivo: e.motivo, detalle: e.detalle }
                : { estado: 'no_impreso', motivo: 'error_agente', detalle: e instanceof Error ? e.message : String(e) };
        }
        // Tras cada intento se refresca el estado para el indicador, sin bloquear.
        void this.sondear();
        return resultado;
    }

    // ------------------------------------------------------ constancia y lista

    /** Deja constancia del estado de la tirilla: PATCH si hay id; Dexie si hay clave. Nunca lanza. */
    async registrar(ref: RefDeVenta | undefined, estado: ReciboEstado, motivo: ReciboMotivo | null = null): Promise<void> {
        if (!ref) return;
        // Vía navegador: no se sabe si salió; no se deja constancia (V52 se queda, sin escrituras).
        if (await this.porNavegador()) return;
        if (ref.clave) {
            try {
                await this.offline.actualizarRecibo(ref.clave, estado, motivo);
            } catch (e) {
                console.error('[impresión] no se pudo guardar el estado de la tirilla en local:', e);
            }
        }
        if (ref.idOrder) {
            try {
                await this.orders.patchRecibo(ref.idOrder, estado, motivo);
            } catch (e) {
                // Backend viejo (404/405) o sin red: la venta ya está cobrada y
                // el estado quedó en local si la venta nació aquí.
                console.error('[impresión] no se pudo guardar el estado de la tirilla en el servidor:', e);
            }
        }
    }

    private async anotarResultado(ref: RefDeVenta | undefined, resultado: ResultadoImpresion,
        orden: OrderResponseRecord, ticket: PosTicketRequest): Promise<void> {
        if (!ref) return;
        if (resultado.estado === 'no_impreso') {
            await this.registrar(ref, 'no_impreso', resultado.motivo ?? 'error_agente');
            await this.anotarPendiente({
                idOrder: ref.idOrder ?? null,
                clave: ref.clave ?? null,
                numero: ticket.ticketNumber,
                hora: orden.createdAt,
                total: orden.total,
                motivo: resultado.motivo ?? 'error_agente',
                detalle: resultado.detalle,
                imprimible: orden,
                ticket,
            });
        } else {
            await this.registrar(ref, resultado.estado, null);
            await this.retirarPendiente(ref);
        }
    }

    private mismaVenta(a: RefDeVenta, b: RefDeVenta): boolean {
        if (a.idOrder && b.idOrder) return a.idOrder === b.idOrder;
        if (a.clave && b.clave) return a.clave === b.clave;
        return false;
    }

    agregarPendiente(v: VentaSinTirilla): void {
        void this.anotarPendiente(v);
    }

    quitarPendiente(ref: RefDeVenta): void {
        void this.retirarPendiente(ref);
    }

    /** En la lista y guardada (F2.5). Guardar es una comodidad: si falla, la lista en memoria vale igual. */
    private async anotarPendiente(v: VentaSinTirilla): Promise<void> {
        this.pendientes.update((lista) => [v, ...lista.filter((p) => !this.mismaVenta(p, v))]);
        const negocio = negocioDeLaSesionAbierta();
        if (!negocio) return;
        try {
            await this.guardadas.guardar(negocio, v);
        } catch (e) {
            console.error('[impresión] no se pudo guardar la venta sin tirilla en local:', e);
        }
    }

    private async retirarPendiente(ref: RefDeVenta): Promise<void> {
        this.pendientes.update((lista) => lista.filter((p) => !this.mismaVenta(p, ref)));
        const negocio = negocioDeLaSesionAbierta();
        if (!negocio) return;
        try {
            await this.guardadas.quitar(negocio, ref);
        } catch (e) {
            console.error('[impresión] no se pudo quitar la venta sin tirilla guardada en local:', e);
        }
    }

    /**
     * Carga del servidor las ventas de HOY sin tirilla. Lo que ya está en la
     * lista (p. ej. una venta offline sin id) se conserva.
     */
    async cargarPendientes(): Promise<void> {
        if (!this.activa()) return;
        if (await this.porNavegador()) return;
        if (!this.session.tieneToken()) return; // en /login no hay ventas que consultar
        // F2.5 — Primero lo guardado en este equipo: las ventas hechas sin red
        // no están en el historial del servidor y, sin esto, se perdían al recargar.
        const negocio = negocioDeLaSesionAbierta();
        if (negocio) {
            try {
                const locales = await this.guardadas.deHoy<VentaSinTirilla>(negocio);
                this.pendientes.update((lista) => [
                    ...lista,
                    ...locales.filter((l) => !lista.some((p) => this.mismaVenta(p, l))),
                ]);
            } catch {
                /* sin almacenamiento local: la lista del servidor sigue valiendo */
            }
        }
        try {
            const res = await this.orders.getHistory(0, 50, undefined, undefined, undefined, undefined, 'no_impreso') as
                { content?: OrderResponseRecord[] } | null;
            const hoy = new Date().toDateString();
            const delServidor = (res?.content ?? [])
                .filter((o) => o.reciboEstado === 'no_impreso' && new Date(o.createdAt).toDateString() === hoy)
                .map<VentaSinTirilla>((o) => ({
                    idOrder: o.idOrder,
                    numero: `INV-${o.idOrder.toString().padStart(5, '0')}`,
                    hora: o.createdAt,
                    total: o.total,
                    motivo: o.reciboMotivo ?? 'error_agente',
                }));
            this.pendientes.update((lista) => {
                const locales = lista.filter((p) => !delServidor.some((s) => this.mismaVenta(s, p)));
                return [...delServidor, ...locales];
            });
        } catch {
            /* backend viejo o sin red: la lista local sigue valiendo */
        }
    }

    /**
     * Reimprime la tirilla de una venta FIEL a la original (ECM, «Reimprimir debe estar»): las líneas de saldo a favor
     * (B3) y la leyenda de venta posterior al inicio (B10) salen de lo que el servidor dijo al guardarla, leído de este
     * equipo. Una venta que no está aquí, o guardada antes de esos campos, sale sin esas líneas, como antes.
     */
    async reimprimirTirilla(orden: OrderResponseRecord, opciones: OpcionesTirilla, ref: RefDeVenta): Promise<ResultadoImpresion> {
        return this.imprimirTirilla(orden, { ...opciones, ...(await this.lineasGuardadas(ref)) }, ref);
    }

    private async lineasGuardadas(ref: RefDeVenta): Promise<Pick<OpcionesTirilla, 'saldoAFavorAplicado' | 'saldoAFavorRestante' | 'ventaPosteriorAlInicio'>> {
        let guardado: Awaited<ReturnType<OfflineOrderService['loQueDijoElServidor']>> = null;
        try {
            guardado = await this.offline.loQueDijoElServidor({ clave: ref.clave ?? null, idOrder: ref.idOrder ?? null });
        } catch {
            /* sin IndexedDB: se reimprime como antes */
        }
        if (!guardado) return {};
        return {
            ...(guardado.saldoAFavorAplicado !== undefined ? { saldoAFavorAplicado: guardado.saldoAFavorAplicado } : {}),
            ...(guardado.saldoAFavorRestante !== undefined ? { saldoAFavorRestante: guardado.saldoAFavorRestante } : {}),
            ...(guardado.ventaPosteriorAlInicio === true ? { ventaPosteriorAlInicio: { venceEl: guardado.venceEl ?? null } } : {}),
        };
    }

    /** Reimprime una venta pendiente, sin cajón. */
    async reimprimir(p: VentaSinTirilla): Promise<ResultadoImpresion> {
        if (p.tipo === 'recibo_cartera') {
            if (!p.ticket) {
                return { estado: 'no_impreso', motivo: 'error_agente', detalle: 'No se encontró el recibo para reimprimir' };
            }
            // Un recibo no es una orden: se reimprime su ticket tal cual, sin cajón.
            const ticket = { ...p.ticket, abrirCajon: false };
            if (await this.porNavegador()) return imprimirEnNavegador.imprimir(htmlDeTirilla(ticket));
            const r = await this.ejecutar(() => this.printer.printTicket(ticket));
            if (r.estado !== 'no_impreso') await this.retirarPendiente(p);
            return r;
        }
        let orden = p.imprimible ?? null;
        if (p.idOrder) {
            try {
                orden = await this.orders.getOrderById(p.idOrder);
            } catch {
                /* sin red: se usa lo local si lo hay */
            }
        }
        if (!orden) {
            return { estado: 'no_impreso', motivo: 'error_agente', detalle: 'No se encontró la venta para reimprimir' };
        }
        const opciones: OpcionesTirilla = {
            abrirCajon: false,
            // Ninguna tirilla lleva resolución (nunca fue real): que la venta pidiera
            // factura se reconoce por los datos del cliente que tecleó el cajero.
            esFactura: !!(p.ticket?.customerId || p.ticket?.customerName),
            cashGiven: p.ticket?.cashGiven,
            change: p.ticket?.change,
            ticketNumber: p.ticket?.ticketNumber,
        };
        return this.reimprimirTirilla(orden, opciones, { idOrder: p.idOrder, clave: p.clave });
    }

    /** Reimprime todas las pendientes, una por una. Devuelve cuántas salieron. */
    async reimprimirTodas(): Promise<number> {
        let ok = 0;
        for (const p of [...this.pendientes()]) {
            const r = await this.reimprimir(p);
            if (r.estado !== 'no_impreso') ok++;
        }
        return ok;
    }

    /** «No hace falta»: la venta queda sin tirilla a propósito. */
    async descartar(p: VentaSinTirilla): Promise<void> {
        if (p.tipo === 'recibo_cartera') {
            await this.retirarPendiente(p);
            return;
        }
        await this.registrar({ idOrder: p.idOrder, clave: p.clave }, 'descartado', null);
        await this.retirarPendiente(p);
    }
}
